import React, { FunctionComponent } from 'react';
import Avatar from '@justgiving/avatar';
import { useTranslation } from 'react-i18next';

import styles from './MemberDetails.scss';
import UserListItem from '../UserListItem/UserListItem';
import PATHS from '../../constants/paths';
import { withTrack, WithTrack } from '../../utils/analytics';
import MemberTotals from '../MemberTotals/MemberTotals';
import classNames from 'classnames';
import { PageStatus } from '../../../types/__generated__';
import { Link } from '../Link/Link';

export type MemberDetailsProps = WithTrack & {
  name: string;
  picture?: string;
  fundraisingPageShortName: string;
  fundraisingPageName: string;
  fundraisingPageIndex: number;
  fundraisingPagesCount: number;
  fundraisingPageStatus: string | PageStatus;
  fundraisingPageTargetAmount: number;
  fundraisingPageRaisedAmount: number;
  fundraisingPageCurrencyCode: string;
  pageUrl: string;
};

export const MemberDetails: FunctionComponent<MemberDetailsProps> = ({
  fundraisingPageIndex,
  fundraisingPageShortName,
  fundraisingPagesCount,
  track,
  pageUrl,
  picture,
  fundraisingPageCurrencyCode,
  fundraisingPageName,
  fundraisingPageRaisedAmount,
  fundraisingPageStatus,
  fundraisingPageTargetAmount,
  name,
}) => {
  const { t } = useTranslation();

  const getTrackingData = () => {
    return {
      event: 'card click',
      action_name: 'card click',
      subtype: 'fundraising page',
      sub_section: 'team members',
      card_rank: fundraisingPageIndex,
      card_count: fundraisingPagesCount,
      card_url: `${PATHS.FRP}${fundraisingPageShortName}`,
    };
  };

  const trackClickFRP = () => {
    const trackingData = {
      event_value: 'team member',
      ...getTrackingData(),
    };
    track(trackingData);
  };

  const trackClickDonate = () => {
    const trackingData = {
      event_value: 'donate',
      ...getTrackingData(),
    };
    track(trackingData);
  };

  const isPageCancelled = fundraisingPageStatus === PageStatus.Cancelled;

  return (
    <UserListItem
      picture={
        <Link
          href={pageUrl}
          tabIndex={-1}
          onClick={trackClickFRP}
          renderAnchor={!isPageCancelled}
        >
          <Avatar avatarSize="auto" picture={picture} />
          <span className="jg-sr-only"> - {fundraisingPageName}</span>
        </Link>
      }
    >
      <div
        className={classNames(styles.memberDetails, {
          [styles.cancelled]: isPageCancelled,
        })}
      >
        <Link
          href={pageUrl}
          onClick={trackClickFRP}
          className={classNames(
            'jg-flex-g1',
            'jg-space-mbxs',
            'jg-text--large-regular',
            { [styles.cancelled]: isPageCancelled },
          )}
          renderAnchor={!isPageCancelled}
        >
          {name}
        </Link>
        <span className="jg-sr-only"> - {fundraisingPageName}</span>

        <MemberTotals
          targetAmount={fundraisingPageTargetAmount}
          raisedAmount={fundraisingPageRaisedAmount}
          currencyCode={fundraisingPageCurrencyCode}
          pageStatus={fundraisingPageStatus}
        />

        <div className={styles.donate}>
          {fundraisingPageShortName &&
            fundraisingPageStatus === PageStatus.Active && (
              <Link
                href={`${pageUrl}/donate`}
                data-qa="frp-donate"
                className={classNames(
                  'jg-display-b',
                  'jg-text--large-regular',
                  'jg-text-color-branded',
                )}
                onClick={trackClickDonate}
              >
                {t('common.donateLink')}
              </Link>
            )}
          {isPageCancelled && <p>{t('common.cancelled')}</p>}
        </div>
      </div>
    </UserListItem>
  );
};

export default withTrack(MemberDetails);
